/* TeleTunes media analytics v1. Native video/audio; consent required; no player control. */
(() => {
  'use strict';
  if (window.__ttMediaAnalytics) return;
  window.__ttMediaAnalytics = { version: '1', active: false };
  const config = document.querySelector('[data-tt-media-config]');
  if (!config) return;
  const context = JSON.parse(config.textContent);
  const states = new Map(), offers = new Set();
  let allowed = false, observer, timer, intersection;
  const clean = value => String(value || '').replace(/[^a-zA-Z0-9._/-]/g, '_').slice(0, 90);
  const hash = value => {
    let result = 2166136261;
    for (const char of value) result = Math.imul(result ^ char.charCodeAt(0), 16777619);
    return (result >>> 0).toString(36);
  };
  const round = value => Math.round(value * 10) / 10;
  function permitted() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function send(state, action, summary = false) {
    if (!allowed || !permitted() || typeof window.clarity !== 'function') return;
    const identity = state.identity;
    // One composite context/action avoids falsely joining different media in a session.
    window.clarity('set', 'tt_media_context', `${identity.key}|${identity.page}|${identity.product}|${identity.asset}|${identity.placement}`);
    window.clarity('set', 'tt_media_action', `${identity.key}|${action}`);
    window.clarity('set', 'tt_media_product', identity.product || 'none');
    window.clarity('set', 'tt_media_asset', identity.asset);
    window.clarity('event', `tt_media_${action}`);
    window.clarity('event', `tt_media_${action}__${identity.key}`);
    if (summary) {
      // Sequence and context make each cumulative snapshot independently interpretable.
      // Fields documented in README; never sum cumulative snapshots as independent views.
      const m = state.media;
      window.clarity('set', 'tt_media_summary', [identity.key, ++state.sequence, action,
        round(m.currentTime || 0), round(Number.isFinite(m.duration) ? m.duration : 0),
        round(state.play), round(state.view), round(state.sound), round(state.foregroundSound),
        round(coverage(state.viewRanges)), round(coverage(state.soundRanges)),
        state.seeks, state.loops, m.playbackRate, m.muted ? 1 : 0, round(m.volume)].join('|'));
    }
  }
  function once(state, action) {
    if (state.sent.has(action)) return;
    state.sent.add(action);
    send(state, action);
  }
  function identity(media, host = media) {
    const raw = media.getAttribute('src') || media.querySelector('source[src]')?.getAttribute('src') || media.currentSrc || '';
    let asset = '';
    try { asset = clean(new URL(raw, location.href).pathname.split('/').pop()); } catch (_) { /* no source yet */ }
    if (!raw || !asset) return null;
    const card = host.closest('.tt-video-card, .video-card, .tt-video-wrap');
    const link = card?.querySelector('a[href*="/products/"]');
    const product = clean(link?.getAttribute('href')?.split('/products/')[1]?.split(/[?#/]/)[0] || context.product);
    const placement = host.closest('tt-sound-demo') ? 'sound-demo' : card ? 'video-card' :
      host.closest('product-media, media-gallery, [data-media-id], deferred-media') ? 'gallery' : clean(media.id) || media.tagName.toLowerCase();
    const page = clean([context.page, context.page_handle].filter(Boolean).join('/'));
    return {asset, product, placement, page, key: hash([page, product, asset, placement].join('|'))};
  }
  function addRange(ranges, start, end) {
    if (!(end > start)) return;
    ranges.push([start, end]); ranges.sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < ranges.length;) {
      if (ranges[i][0] <= ranges[i - 1][1] + 0.05) {
        ranges[i - 1][1] = Math.max(ranges[i - 1][1], ranges[i][1]); ranges.splice(i, 1);
      } else i++;
    }
  }
  const coverage = ranges => ranges.reduce((total, range) => total + range[1] - range[0], 0);
  function isVisible(media) {
    const rect = media.getBoundingClientRect();
    if (!rect.width || !rect.height || media.closest('[hidden], dialog:not([open])')) return false;
    const style = getComputedStyle(media);
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) return false;
    const area = rect.width * rect.height;
    const overlap = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0)) *
      Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
    return overlap / area >= 0.5;
  }
  function tick(state) {
    const m = state.media, now = performance.now(), pos = m.currentTime || 0;
    const elapsed = (now - state.lastTime) / 1000, advance = pos - state.lastPosition;
    const foreground = document.visibilityState === 'visible';
    const visible = m.tagName === 'VIDEO' && isVisible(m);
    if (visible && foreground) once(state, 'impression');
    // Only credit observed progress, never seeks, stalls, hidden slides, or throttled gaps.
    const advancing = state.playing && !m.seeking && !m.paused && m.readyState >= 3 &&
      elapsed > 0 && elapsed <= 1.5 && advance > 0 && advance <= elapsed * Math.max(m.playbackRate, 0.1) + 0.4;
    if (advancing) {
      const seconds = Math.min(elapsed, advance / Math.max(m.playbackRate, 0.1));
      state.play += seconds;
      if (visible && foreground && state.wasVisible && state.wasForeground) {
        state.view += seconds; addRange(state.viewRanges, state.lastPosition, pos); once(state, 'view_start');
      }
      // 'Sound' means the player is unmuted with volume > 0; device output is unknowable.
      if (!m.muted && m.volume > 0 && state.wasAudible) {
        state.sound += seconds;
        if (foreground && state.wasForeground) state.foregroundSound += seconds;
        addRange(state.soundRanges, state.lastPosition, pos); once(state, 'sound_start');
      }
      for (const threshold of [5, 10, 30, 60, 120, 300]) {
        if (state.view >= threshold) once(state, `view_${threshold}s`);
        if (state.sound >= threshold) once(state, `sound_${threshold}s`);
      }
      if (Number.isFinite(m.duration) && m.duration > 0) {
        for (const percent of [25, 50, 75, 95]) {
          if (coverage(state.viewRanges) / m.duration >= percent / 100) once(state, `view_${percent}pct`);
          if (coverage(state.soundRanges) / m.duration >= percent / 100) once(state, `sound_${percent}pct`);
        }
      }
      if (now - state.lastSummary >= 15000) { send(state, 'heartbeat', true); state.lastSummary = now; }
    }
    if (state.playing && !m.seeking && advance < -0.25 && m.loop) {
      state.loops++; send(state, 'loop', true);
    }
    state.lastTime = now; state.lastPosition = pos;
    state.wasForeground = foreground; state.wasVisible = visible; state.wasAudible = !m.muted && m.volume > 0;
  }
  function attach(media) {
    if (states.has(media)) return;
    const id = identity(media); if (!id) return;
    const state = {media, identity: id, sent: new Set(), handlers: [], playing: !media.paused && media.readyState >= 3,
      lastTime: performance.now(), lastPosition: media.currentTime || 0, lastSummary: performance.now(),
      wasForeground: document.visibilityState === 'visible', wasVisible: isVisible(media),
      wasAudible: !media.muted && media.volume > 0, viewRanges: [], soundRanges: [],
      play: 0, view: 0, sound: 0, foregroundSound: 0, seeks: 0, loops: 0, sequence: 0};
    states.set(media, state); intersection?.observe(media);
    if (!offers.has(id.key)) { offers.add(id.key); send(state, 'available'); }
    const listen = (event, fn) => { media.addEventListener(event, fn); state.handlers.push([event, fn]); };
    listen('playing', () => {
      tick(state); state.playing = true;
      once(state, 'start'); once(state, media.autoplay ? 'autoplay' : 'manual_play');
      send(state, 'playing', true);
    });
    listen('timeupdate', () => tick(state));
    listen('pause', () => { tick(state); state.playing = false; send(state, 'pause', true); });
    listen('waiting', () => { tick(state); state.playing = false; send(state, 'buffer', true); });
    listen('seeking', () => {
      const loop = media.loop && Number.isFinite(media.duration) &&
        state.lastPosition >= media.duration - 1.5 && media.currentTime < 1;
      tick(state);
      if (loop) { state.loops++; send(state, 'loop', true); }
      else { state.seeks++; send(state, 'seek', true); }
    });
    listen('seeked', () => { state.lastTime = performance.now(); state.lastPosition = media.currentTime; });
    listen('ended', () => { tick(state); state.playing = false; send(state, 'ended', true); });
    listen('volumechange', () => {
      // Reset the interval at a volume boundary: never credit a muted interval as sound.
      tick(state); send(state, media.muted || media.volume === 0 ? 'mute' : 'unmute', true);
    });
    listen('ratechange', () => { tick(state); send(state, 'rate', true); });
    listen('error', () => { state.playing = false; send(state, 'error', true); });
    if (state.playing) { once(state, 'start'); once(state, media.autoplay ? 'autoplay' : 'manual_play'); }
    tick(state);
  }
  function detach(media, reason) {
    const state = states.get(media); if (!state) return;
    tick(state); if (state.sent.has('start')) send(state, reason, true);
    state.handlers.forEach(([event, fn]) => media.removeEventListener(event, fn));
    intersection?.unobserve(media); states.delete(media);
  }
  function scan() {
    if (!allowed) return;
    for (const [media, state] of states) {
      const next = identity(media);
      if (!media.isConnected || next?.key !== state.identity.key) detach(media, 'removed');
    }
    document.querySelectorAll('video,audio').forEach(attach);
    // Deferred product players are only created after clicking their poster. Count
    // the offer independently of playback, including the sound-demo trigger.
    document.querySelectorAll('template').forEach(template => {
      const media = template.content.querySelector('video,audio');
      const host = template.parentElement;
      if (!media || !host) return;
      const id = identity(media, host);
      if (!id || offers.has(id.key)) return;
      offers.add(id.key); send({identity: id}, 'available');
    });
  }
  function syncConsent() {
    const next = permitted(); if (next === allowed) return;
    allowed = next; window.__ttMediaAnalytics.active = next;
    if (!next) {
      clearInterval(timer); observer?.disconnect(); intersection?.disconnect();
      for (const media of [...states.keys()]) detach(media, 'consent_revoked');
      offers.clear();
      return;
    }
    if ('IntersectionObserver' in window) intersection = new IntersectionObserver(entries => {
      for (const entry of entries) { const state = states.get(entry.target); if (state) tick(state); }
    }, {threshold: [0, 0.5, 1]});
    observer = new MutationObserver(scan);
    observer.observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['src']});
    scan(); timer = setInterval(() => {
      if (!permitted()) { syncConsent(); return; }
      for (const state of states.values()) tick(state);
    }, 500);
  }
  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('visibilitychange', () => {
    for (const state of states.values()) { tick(state); if (state.sent.has('start')) send(state, 'visibility', true); }
  });
  window.addEventListener('pagehide', () => {
    for (const state of states.values()) { tick(state); if (state.sent.has('start')) send(state, 'exit', true); }
  });
  document.addEventListener('click', event => {
    if (!allowed) return;
    const link = event.target.closest('a[href]');
    const card = link?.closest('.tt-video-card, .video-card, .tt-video-wrap');
    const state = card && states.get(card.querySelector('video,audio'));
    if (state) {
      const href = link.getAttribute('href');
      if (/\/products\//.test(href)) send(state, 'product_click', true);
      else if (/^https:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(href)) send(state, 'youtube_click', true);
    }
  }, {capture: true});
  // Shopify owns permission state; do not infer consent from missing APIs or country.
  if (window.Shopify?.customerPrivacy) syncConsent();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => { if (!error) syncConsent(); });
  }
})();
